/**
 * ChatMessage Component
 * Renders individual chat messages with tool buttons support
 */

import React from "react";
import { Box, Typography, Button, Tooltip, CircularProgress, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CHAT_TOOLS } from "../../services/chatToolsService";
import { createToolButtonRegex } from "./utils/toolParsing";
import { parseToolMarkerValue } from "./utils/toolParsing";
import MarkdownContent from "./MarkdownContent";

/**
 * ChatMessageContent Component
 * Renders message content with tool buttons
 * @param {Object} props - Component props
 * @param {Object} props.message - Message object
 * @param {string} props.toolLoadingId - Currently loading tool ID
 * @param {boolean} props.isDisabled - Whether interactions are disabled
 * @param {Function} props.onToolClick - Callback when tool button is clicked
 * @returns {JSX.Element} Rendered message content
 */
const ChatMessageContent = ({ message, toolLoadingId, isDisabled, onToolClick }) => {
        const { t } = useTranslation();
        const theme = useTheme();
        const content = message?.content || "";
        const regex = createToolButtonRegex();
        const elements = [];
        let lastIndex = 0;
        let match;

        while ((match = regex.exec(content)) !== null) {
                const [fullMatch, toolId, customLabel] = match;
                const textSegment = content.slice(lastIndex, match.index);

                if (textSegment) {
                        elements.push({ type: "text", value: textSegment });
                }

                const parsedMarker = parseToolMarkerValue(customLabel);
                elements.push({
                        type: "tool",
                        toolId,
                        label: parsedMarker.label,
                        payload: parsedMarker.payload,
                        raw: customLabel,
                });
                lastIndex = match.index + fullMatch.length;
        }

        const trailingText = content.slice(lastIndex);

        if (trailingText) {
                elements.push({ type: "text", value: trailingText });
        }

        if (elements.length === 0) {
                return (
                        <MarkdownContent>{content}</MarkdownContent>
                );
        }

        return (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {elements.map((element, index) => {
                                if (element.type === "text") {
                                        return (
                                                <MarkdownContent key={`${message.id}-text-${index}`}>
                                                        {element.value}
                                                </MarkdownContent>
                                        );
                                }

                                const tool = CHAT_TOOLS[element.toolId];
                                const labelText =
                                        (typeof element.label === "string" && element.label.trim())
                                                ? element.label.trim()
                                                : tool
                                                        ? t(tool.labelKey)
                                                        : element.toolId;
                                const isCurrentLoading = toolLoadingId === element.toolId;

                                if (!tool) {
                                        return (
                                                <Typography
                                                        key={`${message.id}-unknown-${index}`}
                                                        variant="body2"
                                                        sx={{ whiteSpace: "pre-wrap" }}
                                                >
                                                        {labelText}
                                                </Typography>
                                        );
                                }

                                return (
                                        <Tooltip
                                                key={`${message.id}-tool-${element.toolId}-${index}`}
                                                title={tool.descriptionKey ? t(tool.descriptionKey) : ""}
                                                arrow
                                        >
                                                <span>
                                                        <Button
                                                                variant="contained"
                                                                size="small"
                                                                color="primary"
                                                                disabled={isDisabled || isCurrentLoading}
                                                                onClick={() =>
                                                                        onToolClick?.(element.toolId, {
                                                                                customLabel: labelText,
                                                                                source: "message-action",
                                                                                payload: element.payload ?? null,
                                                                        })
                                                                }
                                                                sx={{ textTransform: "none" }}
                                                        >
                                                                {isCurrentLoading ? (
                                                                        <CircularProgress
                                                                                size={16}
                                                                                sx={{ color: theme.palette.common.white }}
                                                                        />
                                                                ) : (
                                                                        labelText
                                                                )}
                                                        </Button>
                                                </span>
                                        </Tooltip>
                                );
                        })}
                </Box>
        );
};

export default ChatMessageContent;

